import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Constant } from '../../../models/constant';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import { useTheme, useMediaQuery, Tooltip, Button, Box, Card, CardContent } from '@mui/material';
import { Build as BuildingIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import useAxiosInterceptor from '../../AxiosInterceptor';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
    stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
    iconActionColumnSizing,
    simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const Constantes: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    useAxiosInterceptor();
    document.title = "Administrar Constantes";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [constants, setConstants] = useState<Constant[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const { drawerOpen } = useDrawer();
    const { canCreate, canUpdate, canDelete, canAssign } = useResourcePermissions('/administrar/constantes');

    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                const { data } = await axios.get('constants');
                setConstants(data.data);
                await new Promise((setEmployees) => setTimeout(setEmployees, 1000));
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                showAlert('Error fetching data!', 'error');
            } finally {
                hideLoading();
            }
        };
        fetchData();
    }, []);

    const handleClickOpen = (id: number) => {
        if (!canDelete) return;

        setSelectedId(id);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedId(null);
    };

    const handleDelete = async () => {
        if (!canDelete) return;

        if (selectedId !== null) {
            try {
                const response = await axios.delete(`constants/${selectedId}`);

                if (response.status === 204) { // Verificamos si la eliminación fue exitosa
                    showAlert('Registro eliminado exitosamente!', 'success');
                    setConstants(constants.filter((constant: Constant) => constant.id !== selectedId));
                } else {
                    showAlert('No se pudo eliminar el registro. Por favor, inténtalo nuevamente.', 'warning');
                }
            } catch (error) {
                console.error('Error al eliminar el registro:', error);

                // Chequeo seguro del tipo de error
                if (axios.isAxiosError(error)) {
                    if (error.response && error.response.status === 409) {
                        showAlert('No se puede eliminar el registro porque está siendo utilizado por otro recurso.', 'error');
                    } else {
                        showAlert('Error al eliminar el registro. Por favor, inténtalo más tarde.', 'error');
                    }
                } else {
                    showAlert('Ocurrió un error inesperado. Por favor, inténtalo más tarde.', 'error');
                }
            } finally {
                handleClose();
            }
        }
    };

    const simpleColumns: SimpleColumn[] = [
        { field: 'name', headerName: 'Nombre', minWidth: 150, flex: 1, wrapText: true },
        { field: 'utility', headerName: 'Utilidad', minWidth: 150, flex: 1 },
        { field: 'term', headerName: 'Término', minWidth: 150, flex: 1 },
        { field: 'cloveday', headerName: 'Días Clavo', minWidth: 150, flex: 1 },
        { field: 'type_constant', headerName: 'Tipo Constante', minWidth: 170, flex: 1, wrapText: true },
        {
            field: 'opt_edit',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => (
                <PermissionButton
                    allowed={canUpdate}
                    deniedTooltip="Sin permiso para editar"
                    tooltip="Editar"
                    variant="contained"
                    color="primary"
                    sx={simpleTableIconButtonSx}
                    component={NavLink}
                    to={`/administrar/constantes/${row.id}/editar`}
                >
                    <EditIcon fontSize="small" />
                </PermissionButton>
            ),
        },
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => (
                <PermissionButton
                    allowed={canDelete}
                    deniedTooltip="Sin permiso para eliminar"
                    tooltip="Eliminar"
                    variant="contained"
                    color="error"
                    sx={simpleTableIconButtonSx}
                    onClick={() => handleClickOpen(Number(row.id))}
                >
                    <DeleteIcon fontSize="small" />
                </PermissionButton>
            ),
        },
    ];

    const rows: SimpleRow[] = constants.map((constant: Constant) => ({
        id: constant.id as RowId,
        name: constant.name,
        utility: constant.utility,
        term: constant.term,
        cloveday: constant.cloveday,
        type_constant: constant.type_constant.name,
    }));

    return (
        <HeaderResponsive>
            <StickyListToolbar>
                <Title>Información Constantes</Title>
                <Box sx={{ ml: 'auto' }}>
                    <PermissionButton
                        allowed={canAssign}
                        deniedTooltip="Sin permiso para asignar"
                        tooltip="Asignar"
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={<BuildingIcon />}
                        component={NavLink}
                        to="/administrar/constantes/assign"
                    >
                        {isMobile ? null : 'Asignar'}
                    </PermissionButton>
                    <PermissionButton
                        allowed={canCreate}
                        deniedTooltip="Sin permiso para crear"
                        tooltip="Agregar"
                        size="small"
                        variant="contained"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to="/administrar/constantes/add"
                    >
                        {isMobile ? <AddIcon /> : 'Agregar'}
                    </PermissionButton>
                </Box>
            </StickyListToolbar>
            <Card>
                <CardContent
                    sx={{
                        width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'auto',
                        pt: 0,
                        pb: 4,
                    }}
                >
                    <Box>
                        {constants.length > 0 ? (
                            <SimpleCustomTable
                                rows={rows}
                                columns={simpleColumns}
                                disableInternalScroll
                                showBorders
                                showCount
                                exportCsv
                                exportFileName="constantes.csv"
                                wrapCellContent
                                columnGroups={[{ header: 'Opciones', fields: ['opt_edit', 'opt_delete'] }]}
                            />
                        ) : (
                            <EmptyStateMessage message="No existen registros" align="left" />
                        )}
                    </Box>

                    {/* Modal de Confirmación de Eliminación */}
                    <Dialog open={open} onClose={handleClose}>
                        <DialogTitle>Confirmación</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                ¿Estás seguro de que deseas eliminar este registro?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose} color="primary">Cancelar</Button>
                            <PermissionButton
                                allowed={canDelete}
                                deniedTooltip="Sin permiso para eliminar"
                                tooltip="Eliminar"
                                onClick={handleDelete}
                                color="secondary"
                            >
                                Eliminar
                            </PermissionButton>
                        </DialogActions>
                    </Dialog>
                </CardContent>
            </Card>
            <Box sx={{ height: { xs: 48, md: 32 } }} />
        </HeaderResponsive >
    );
};

export default Constantes;
